import React, { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';
import PasswordInput from '../components/PasswordInput';
import PasswordStrengthBar from '../components/PasswordStrengthBar';
import PasswordRequirements from '../components/PasswordRequirements';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import { changePassword } from '../api/authApi';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../types/auth.types';
import styles from './ChangePasswordPage.module.css';

export default function ChangePasswordPage() {
  const [searchParams] = useSearchParams();
  const isExpiredFlow = searchParams.get('expired') === 'true';
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();

  // El flujo de contraseña expirada llega sin sesión activa (el login nunca emitió
  // tokens), por eso esta página admite acceso sin autenticar solo en ese caso.
  useEffect(() => {
    if (!isAuthenticated && !isExpiredFlow) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isExpiredFlow, navigate]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ currentPassword?: string; confirmPassword?: string }>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function validate(): boolean {
    const errors: { currentPassword?: string; confirmPassword?: string } = {};
    if (!isExpiredFlow && !currentPassword) {
      errors.currentPassword = 'Ingresá tu contraseña actual.';
    }
    if (!newPassword.trim() || !confirmPassword.trim()) {
      errors.confirmPassword = 'Completá los dos campos de contraseña.';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setGeneralError(null);
    setSuccessMessage(null);

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const response = await changePassword({
        currentPassword: isExpiredFlow ? undefined : currentPassword,
        newPassword,
        confirmPassword,
      });
      setSuccessMessage(response.mensaje);
      setTimeout(() => {
        logout().finally(() => navigate('/login', { replace: true }));
      }, 1800);
    } catch (error) {
      if (axios.isAxiosError<ApiError>(error)) {
        setGeneralError(error.response?.data?.mensaje ?? 'No se pudo actualizar la contraseña.');
      } else {
        setGeneralError('No se pudo actualizar la contraseña.');
      }
    } finally {
      setLoading(false);
    }
  }

  const banner = isExpiredFlow ? (
    <div className={styles.expiredBanner}>
      Tu contraseña ha expirado. Debés establecer una nueva para continuar.
    </div>
  ) : undefined;

  return (
    <AuthLayout logoSize={150} title="Cambiar contraseña" banner={banner}>
      {successMessage ? (
        <p className={styles.success}>{successMessage}</p>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          {generalError && <ErrorBanner message={generalError} onClose={() => setGeneralError(null)} />}

          {!isExpiredFlow && (
            <PasswordInput
              id="currentPassword"
              label="Contraseña actual"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              error={fieldErrors.currentPassword}
            />
          )}

          <PasswordInput
            id="newPassword"
            label="Nueva contraseña"
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
          />
          <PasswordStrengthBar password={newPassword} />
          <PasswordRequirements password={newPassword} />

          <PasswordInput
            id="confirmPassword"
            label="Confirmar nueva contraseña"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            error={fieldErrors.confirmPassword}
          />

          <Button type="submit" loading={loading} loadingText="Actualizando...">
            Actualizar contraseña
          </Button>

          {!isExpiredFlow && (
            <div className={styles.backLinkWrapper}>
              <Link to="/mi-cuenta" className={styles.backLink}>
                Volver
              </Link>
            </div>
          )}
        </form>
      )}
    </AuthLayout>
  );
}
